import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, ImagePlus, Building2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { createCompany, deleteCompany } from "@/lib/api";

export default function CompaniesPage({ companies, loading, onChanged }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setName("");
    setLogoFile(null);
    setLogoPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Logo must be under 5MB");
      return;
    }
    setLogoFile(f);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createCompany({ name: name.trim(), logoFile });
      toast.success(`${created.name} created`);
      setOpen(false);
      reset();
      await onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create company");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (company) => {
    try {
      await deleteCompany(company.id);
      toast.success(`${company.name} deleted`);
      await onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete company");
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">Companies</h1>
          <p className="text-neutral-500 mt-1 text-sm max-w-xl">
            Each company has its own logo and set of saved item presets. Select one to start generating A6 stickers.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-button" className="gap-2">
              <Plus className="h-4 w-4" /> New company
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New company</DialogTitle>
              <DialogDescription>Add a company and upload its logo. The logo will appear on every sticker.</DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Name</Label>
                <Input
                  id="company-name"
                  data-testid="company-name-input"
                  placeholder="e.g. Artmatter"
                  value={name}
                  maxLength={80}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label>Logo (optional)</Label>
                <div className="flex items-center gap-3">
                  <div className="h-16 w-16 rounded-md border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img src={logoPreview} alt="preview" className="max-h-full max-w-full object-contain" />
                    ) : (
                      <ImagePlus className="h-5 w-5 text-neutral-400" />
                    )}
                  </div>
                  <div className="space-y-1">
                    <input
                      ref={fileRef}
                      data-testid="company-logo-input"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={onPickLogo}
                      className="block text-sm text-neutral-600 file:mr-3 file:rounded-md file:border-0 file:bg-neutral-900 file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:bg-neutral-800"
                    />
                    <p className="text-[11px] text-neutral-500">PNG, JPG, or WEBP · max 5MB</p>
                  </div>
                </div>
              </div>
              <DialogFooter className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setOpen(false); reset(); }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="company-submit-button"
                  disabled={submitting}
                >
                  {submitting ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-44 rounded-lg border border-neutral-200 bg-neutral-50 animate-pulse" />
          ))}
        </div>
      ) : companies.length === 0 ? (
        <div className="dot-grid rounded-xl border border-dashed border-neutral-300 p-12 text-center">
          <div className="mx-auto h-12 w-12 rounded-md bg-white border border-neutral-200 flex items-center justify-center mb-4">
            <Building2 className="h-5 w-5 text-neutral-500" />
          </div>
          <h3 className="text-base font-semibold text-neutral-900">No companies yet</h3>
          <p className="text-sm text-neutral-500 mt-1">Create your first company to start generating A6 stickers.</p>
          <div className="mt-4">
            <Button onClick={() => setOpen(true)} data-testid="empty-add-company-button" className="gap-2">
              <Plus className="h-4 w-4" /> New company
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c) => (
            <div
              key={c.id}
              data-testid={`company-card-${c.id}`}
              className="group relative rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all p-5 flex flex-col"
            >
              <button
                onClick={() => navigate(`/c/${c.id}`)}
                className="absolute inset-0 rounded-xl"
                data-testid={`open-company-${c.id}`}
                aria-label={`Open ${c.name}`}
              />
              <div className="flex items-center gap-3 relative pointer-events-none">
                {c.logo_data_url ? (
                  <img
                    src={c.logo_data_url}
                    alt={c.name}
                    className="h-12 w-12 object-contain rounded-md border border-neutral-200 bg-white p-1"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-md bg-neutral-100 border border-neutral-200 flex items-center justify-center text-sm font-semibold text-neutral-500">
                    {c.name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-neutral-900 truncate">{c.name}</div>
                  <div className="text-xs text-neutral-500">
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between relative">
                <span className="inline-flex items-center gap-1 text-sm text-neutral-700 group-hover:text-neutral-900 pointer-events-none">
                  Open workspace <ChevronRight className="h-4 w-4" />
                </span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="relative h-8 w-8 p-0 text-neutral-400 hover:text-red-600"
                      data-testid={`delete-company-${c.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently deletes the company and all of its saved presets. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDelete(c)}
                        data-testid={`confirm-delete-company-${c.id}`}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
